/**
 * Frontend dispatch helpers for billing-related backend errors.
 *
 * The runner returns 402 with body ``{ error: "IncludedTokensExceeded", ... }``
 * when an org on a finite plan (Starter / Pro / Ultra) has met its monthly
 * included-tokens cap. The Run UI dispatches on the error CODE, not the
 * copy — copy is server-owned and may evolve.
 *
 * Mirror the gate matrix in ``backend/api/sls/lambda-python3.9/layers/
 * stepflow_core/python/stepflow_core/org_credit_gate.py``.
 */

export const BILLING_ERROR_CODES = Object.freeze({
  INCLUDED_TOKENS_EXCEEDED: 'IncludedTokensExceeded',
});

/**
 * Sentinel error subclass thrown by ``handleExecuteResponse`` when the
 * backend returns a 402 IncludedTokensExceeded. Callers ``instanceof``
 * check this to render the upgrade UI; everything else falls through to
 * the generic "execution failed" toast.
 */
export class IncludedTokensExceededError extends Error {
  constructor(message) {
    super(message || 'This organization has used its included tokens for this billing period.');
    this.name = 'IncludedTokensExceededError';
    this.code = BILLING_ERROR_CODES.INCLUDED_TOKENS_EXCEEDED;
    this.status = 402;
  }
}

/**
 * Inspect a parsed response body and decide whether it represents the
 * 402 IncludedTokensExceeded case. Centralizing the predicate here means
 * a backend rename would only need a single update.
 */
export function isIncludedTokensExceeded(status, body) {
  if (status !== 402) return false;
  if (!body || typeof body !== 'object') return false;
  return body.error === BILLING_ERROR_CODES.INCLUDED_TOKENS_EXCEEDED;
}

/**
 * Inspect ``fetch`` Response + parsed JSON; throw the typed error if it's
 * the 402 budget case, otherwise throw a generic ``Error`` carrying the
 * server's ``error``/``message`` (preserving existing fetch UX).
 *
 * Callers should ``await response.json().catch(() => ({}))`` and pass the
 * (status, body) pair in. This keeps the helper isomorphic — no Response
 * coupling — so it's trivially unit-testable.
 */
export function throwForExecuteResponse(status, body) {
  if (isIncludedTokensExceeded(status, body)) {
    throw new IncludedTokensExceededError(body?.message);
  }
  // ROOM_BUSY (409, single-flight gate) has a human ``message`` ("A run is
  // already in progress…") that reads far better than the ``error`` code —
  // prefer the message so the room's system-event backstop is legible. Other
  // errors keep the existing error-first precedence.
  const isRoomBusy = status === 409 && (body?.error === 'ROOM_BUSY');
  const msg = isRoomBusy
    ? (body?.message || 'A run is already in progress in this room.')
    : (body?.error || body?.message || `HTTP ${status}`);
  throw new Error(msg);
}
